/**
 * Algolia Multi-Index Search Integration (Articles + Products)
 * Powers both predictive search (header dropdown) and the /search results page.
 * Overrides the default Shopify search entirely when Algolia is configured.
 */
(function () {
  'use strict';

  // ==========================================
  // CONFIGURATION & INITIALIZATION
  // ==========================================

  if (!window.algoliaConfig) {
    console.warn('Algolia config not found');
    return;
  }

  const {
    appId,
    searchKey,
    indexName: articleIndexName = 'petrolicious_articles',
    productIndexName = 'petrolicious_products',
    hitsPerPage = 6,
    searchPageEnabled = true,
    searchPageResults = 24
  } = window.algoliaConfig;

  if (!appId || !searchKey) {
    console.warn('Algolia credentials incomplete');
    return;
  }

  const ARTICLE_ATTRIBUTES = [
    'objectID', 'title', 'url', 'handle',
    'image', 'featured_image', 'published_at',
    'created_at', 'author', 'blog_handle', 'blog'
  ];

  const PRODUCT_ATTRIBUTES = [
    'objectID', 'title', 'url', 'handle',
    'image', 'featured_image', 'vendor',
    'product_type', 'price', 'published_at'
  ];

  let algoliaClient = null;

  function initAlgolia() {
    if (typeof algoliasearch === 'undefined') {
      setTimeout(initAlgolia, 100);
      return;
    }
    
    // Initialize Algolia v4 Client for Multi-Index Queries
    algoliaClient = algoliasearch(appId, searchKey);

    overridePredictiveSearch();
    initSearchResultsPage();
  }

  // ==========================================
  // SHARED UTILITIES
  // ==========================================

  function highlightText(text, searchTerms) {
    if (!text || !searchTerms) return text || '';
    const terms = searchTerms.toLowerCase().split(/\s+/).filter(t => t.length > 0);
    let result = text;
    terms.forEach(term => {
      const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      result = result.replace(regex, '<mark>$1</mark>');
    });
    return result;
  }

  function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  function formatPrice(price) {
    if (price === null || price === undefined) return '';
    return `$${parseFloat(price).toFixed(2)}`;
  }

  function getArticleUrl(hit) {
    if (hit.url) return hit.url;
    const blogHandle = hit.blog_handle || (hit.blog && hit.blog.handle);
    if (blogHandle && hit.handle) return `/blogs/${blogHandle}/${hit.handle}`;
    return '#';
  }

  function getProductUrl(hit) {
    if (hit.url) return hit.url;
    if (hit.handle) return `/products/${hit.handle}`;
    return '#';
  }

  // ==========================================
  // CARD RENDERERS (ARTICLES & PRODUCTS)
  // ==========================================

  function renderArticleCard(hit) {
    const imageUrl = hit.image || hit.featured_image || '';
    const title = hit.title || '';
    const url = getArticleUrl(hit);

    const authorName = hit.author
      ? (typeof hit.author === 'object' ? (hit.author.name || '') : hit.author)
      : 'Petrolicious';
    let publishDate = '';
    if (hit.published_at || hit.created_at) {
      const dateObj = new Date(hit.published_at || hit.created_at);
      publishDate = dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }

    return `
      <article class="card card--article card--style-padding card--text-display--title is_here">
        <a class="card__image" href="${url}">
          <figure class="aspect-ratio--landscape aspect-ratio__mobile--landscape">
            ${imageUrl ? `<img src="${imageUrl}" alt="${escapeHtml(title)}" class="img-absolute" loading="lazy" width="400" height="300">` : ''}
          </figure>
        </a>
        <a href="${url}" class="card__content">
          <div>
            ${authorName || publishDate ? `<p class="card__blog-title text-xs">by ${escapeHtml(authorName)}${publishDate ? ` – ${publishDate}` : ''}</p>` : ''}
            <p class="card__title">${escapeHtml(title)}</p>
          </div>
          <div class="card__bottom">
            <p class="button button--primary">${window.searchStrings?.read_more || 'Read More'}</p>
          </div>
        </a>
      </article>
    `;
  }

  function renderProductCard(hit) {
    const imageUrl = hit.image || hit.featured_image || '';
    const title = hit.title || '';
    const url = getProductUrl(hit);
    const priceFormatted = formatPrice(hit.price);

    return `
      <a href="${url}" class="card--product-horizontal">
        <div class="card__image-thumb">
          ${imageUrl ? `<img src="${imageUrl}" alt="${escapeHtml(title)}" loading="lazy" width="70" height="70">` : ''}
        </div>
        <div class="card__details">
          ${hit.vendor ? `<p class="card__vendor">${escapeHtml(hit.vendor)}</p>` : ''}
          <p class="card__title">${escapeHtml(title)}</p>
          <p class="card__price">${priceFormatted || '$0.00'}</p>
        </div>
      </a>
    `;
  }

  // ==========================================
  // PREDICTIVE SEARCH — SIDEBAR & MULTI-GRID
  // ==========================================

  function renderSidebar(articleHits, productHits, searchTerms) {
    let listHtml = '';

    // Products Section in Sidebar
    if (productHits.length > 0) {
      const productItems = productHits.map(hit => {
        const title = hit._highlightResult?.title?.value || highlightText(hit.title, searchTerms);
        return `<li class="searches-search__item"><a href="${getProductUrl(hit)}">${title}</a></li>`;
      }).join('');
      listHtml += `
        <div class="search-sidebar__list mb-4">
          <p class="search-sidebar__header text-md">${window.searchStrings?.products || 'Products'}</p>
          <ul class="product-search">${productItems}</ul>
        </div>
      `;
    }

    // Articles Section in Sidebar
    if (articleHits.length > 0) {
      const articleItems = articleHits.map(hit => {
        const title = hit._highlightResult?.title?.value || highlightText(hit.title, searchTerms);
        return `<li class="searches-search__item"><a href="${getArticleUrl(hit)}">${title}</a></li>`;
      }).join('');
      listHtml += `
        <div class="search-sidebar__list">
          <p class="search-sidebar__header text-md">${window.searchStrings?.articles || 'Articles'}</p>
          <ul class="article-search">${articleItems}</ul>
        </div>
      `;
    }

    if (articleHits.length === 0 && productHits.length === 0) {
      listHtml = `
        <div class="search-sidebar__list">
          <p class="search-sidebar__header text-md">${window.searchStrings?.title_no_results || 'No results found'}</p>
        </div>
      `;
    }

    return `<div class="search-sidebar__results">${listHtml}</div>`;
  }

  function renderGrid(articleHits, productHits, searchTerms) {
    if (articleHits.length === 0 && productHits.length === 0) {
      return `
        <div class="no-results-notice">
          <p>${window.searchStrings?.no_results_predictive || 'No results found for your search.'}</p>
        </div>
      `;
    }

    // Sort Articles by publish date
    const sortedArticles = [...articleHits].sort((a, b) => {
      return new Date(b.published_at || 0) - new Date(a.published_at || 0);
    });

    const productCardsHtml = productHits.map(hit => renderProductCard(hit)).join('');
    const articleCardsHtml = sortedArticles.map(hit => renderArticleCard(hit)).join('');

    let viewAllHtml = '';
    if (articleHits.length >= hitsPerPage || productHits.length >= hitsPerPage) {
      const encodedTerms = encodeURIComponent(searchTerms);
      viewAllHtml = `
        <div class="search-view-all">
          <a href="/search?q=${encodedTerms}" class="button button--primary w-full">
            ${window.searchStrings?.view_all || 'View all results'}
          </a>
        </div>
      `;
    }

    return `
      <div class="search-products__row">
        <div class="search-multi-col">
          
          <!-- Column 1: Articles (FIRST / Main Column) -->
          <div class="search-col search-col--articles">
            <p class="search-col__heading">${window.searchStrings?.articles || 'Articles'}</p>
            ${articleHits.length > 0
              ? `<div class="search-products__grid is_hereeee search-products__grid--articles" tabindex="-1">${articleCardsHtml}</div>`
              : `<p class="text-xs text-muted">No articles found</p>`
            }
          </div>

          <!-- Column 2: Products (SECOND / Last Column - 1 per row, Image Left, Details Right) -->
          <div class="search-col search-col--products">
            <p class="search-col__heading">${window.searchStrings?.products || 'Products'}</p>
            ${productHits.length > 0 
              ? `<div class="search-products__grid--products">${productCardsHtml}</div>`
              : `<p class="text-xs text-muted">No products found</p>`
            }
          </div>

        </div>
        ${viewAllHtml}
      </div>
    `;
  }

  // ==========================================
  // PREDICTIVE SEARCH OVERRIDE (BATCH MULTI-INDEX)
  // ==========================================

  function overridePredictiveSearch() {
    if (!customElements.get('predictive-search')) {
      setTimeout(overridePredictiveSearch, 100);
      return;
    }

    document.querySelectorAll('predictive-search').forEach(element => {
      element.search = async function (event) {
        const query = event.target.value;

        if (!query || !algoliaClient) return;

        const resultsContainer = this.querySelector('.predictive-search__results-container');
        if (!resultsContainer) return;

        resultsContainer.classList.add('loading');

        try {
          // Batch Multi-Index Query to Algolia
          const { results } = await algoliaClient.search([
            {
              indexName: productIndexName,
              query: query,
              params: {
                hitsPerPage: Math.min(hitsPerPage, 4),
                attributesToRetrieve: PRODUCT_ATTRIBUTES,
                attributesToHighlight: ['title']
              }
            },
            {
              indexName: articleIndexName,
              query: query,
              params: {
                hitsPerPage: hitsPerPage,
                attributesToRetrieve: ARTICLE_ATTRIBUTES,
                attributesToHighlight: ['title']
              }
            }
          ]);

          const productHits = results[0]?.hits || [];
          const articleHits = results[1]?.hits || [];

          // Update sidebar (suggestions list)
          const sidebar = resultsContainer.querySelector('.search-sidebar');
          if (sidebar) {
            let sidebarInner = sidebar.querySelector('#search-sidebar');
            if (!sidebarInner) {
              sidebarInner = document.createElement('div');
              sidebarInner.id = 'search-sidebar';
              sidebarInner.classList.add('search-sidebar__initial');
              sidebar.prepend(sidebarInner);
            }
            const existingResults = sidebar.querySelector('.search-sidebar__results');
            if (existingResults) existingResults.remove();
            sidebar.insertAdjacentHTML('beforeend', renderSidebar(articleHits, productHits, query));

            if (this.populateRecentSearches) {
              this.populateRecentSearches(resultsContainer);
            }
          }

          // Update grid (cards row)
          const productsSection = resultsContainer.querySelector('.search-products');
          if (productsSection) {
            productsSection.innerHTML = renderGrid(articleHits, productHits, query);
          }

          // Update recent searches
          const allPredictiveSearch = document.querySelectorAll('predictive-search');
          allPredictiveSearch.forEach(ps => {
            if (ps.recentSearches && !ps.recentSearches.includes(query)) {
              if (ps.recentSearches.length > 4) {
                ps.recentSearches.pop();
              }
              ps.recentSearches.unshift(query);
              sessionStorage.setItem('recentSearches', JSON.stringify(ps.recentSearches));
            }
            if (ps.addRecentSearchesListeners) {
              ps.addRecentSearchesListeners();
            }
          });

        } catch (error) {
          console.error('Algolia predictive search error:', error);
        } finally {
          resultsContainer.classList.remove('loading');
        }
      };
    });

    console.log('Algolia multi-index predictive search active');
  }

  // ==========================================
  // SEARCH RESULTS PAGE (/search)
  // ==========================================
  const RESULTS_PER_PAGE = searchPageResults;
  let currentPage = 0;
  let currentQuery = '';
  let totalPages = 0;

  function isSearchResultsPage() {
    return document.body.classList.contains('template-search') ||
      window.location.pathname === '/search';
  }

  function renderPagination(currentPage, totalPages) {
    if (totalPages <= 1) return '';

    if (!document.querySelector('link[href*="component-pagination.css"]')) {
      const cssLink = document.createElement('link');
      cssLink.rel = 'stylesheet';
      cssLink.href = window.Shopify?.routes?.root
        ? `${window.Shopify.routes.root}cdn/shop/t/1/assets/component-pagination.css`
        : '/cdn/shop/t/1/assets/component-pagination.css';
      document.head.appendChild(cssLink);
    }

    let html = '<nav class="pagination" role="navigation" aria-label="Pagination">';
    html += '<ul role="list">';

    if (currentPage > 0) {
      html += `
        <li class="prev">
          <a href="#" data-page="${currentPage - 1}" aria-label="Previous page">
            &larr; <p class="hide-mobile">Previous</p>
          </a>
        </li>
      `;
    }

    const maxVisible = 5;
    let startPage = Math.max(0, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages - 1, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(0, endPage - maxVisible + 1);
    }

    if (startPage > 0) {
      html += `<li><a href="#" data-page="0">1</a></li>`;
      if (startPage > 1) {
        html += '<li><span>...</span></li>';
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      if (i === currentPage) {
        html += `
          <li>
            <span class="pagination__item--current" aria-current="page">${i + 1}</span>
          </li>
        `;
      } else {
        html += `<li><a href="#" data-page="${i}">${i + 1}</a></li>`;
      }
    }

    if (endPage < totalPages - 1) {
      if (endPage < totalPages - 2) {
        html += '<li><span>...</span></li>';
      }
      html += `<li><a href="#" data-page="${totalPages - 1}">${totalPages}</a></li>`;
    }

    if (currentPage < totalPages - 1) {
      html += `
        <li class="next">
          <a href="#" data-page="${currentPage + 1}" aria-label="Next page">
            <p class="hide-mobile">Next</p> &rarr;
          </a>
        </li>
      `;
    }

    html += '</ul></nav>';
    return html;
  }

  function attachPaginationListeners() {
    document.querySelectorAll('.pagination a[data-page]').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const page = parseInt(link.dataset.page, 10);
        performSearchPageSearch(currentQuery, page);
      });
    });
  }

  async function performSearchPageSearch(query, page = 0) {
    if (!algoliaClient || !query) return;

    currentQuery = query;
    currentPage = page;

    const gridContainer = document.getElementById('grid-product');
    const productGridContainer = document.getElementById('ProductGridContainer');

    if (!gridContainer) return;

    gridContainer.style.opacity = '0.3';
    gridContainer.style.pointerEvents = 'none';

    try {
      const halfPageLimit = Math.max(1, Math.floor(RESULTS_PER_PAGE / 2));

      const { results } = await algoliaClient.search([
        {
          indexName: productIndexName,
          query: query,
          params: {
            hitsPerPage: halfPageLimit,
            page: page,
            attributesToRetrieve: PRODUCT_ATTRIBUTES,
            attributesToHighlight: ['title']
          }
        },
        {
          indexName: articleIndexName,
          query: query,
          params: {
            hitsPerPage: halfPageLimit,
            page: page,
            attributesToRetrieve: ARTICLE_ATTRIBUTES,
            attributesToHighlight: ['title']
          }
        }
      ]);

      const productHits = results[0]?.hits || [];
      const articleHits = results[1]?.hits || [];
      const totalHits = (results[0]?.nbHits || 0) + (results[1]?.nbHits || 0);
      totalPages = Math.max(results[0]?.nbPages || 0, results[1]?.nbPages || 0);

      const heading = document.querySelector('.search-results h1');
      if (heading && totalHits > 0) {
        heading.textContent = window.searchStrings?.title_results || 'Search results';
      }

      if (totalHits === 0) {
        gridContainer.innerHTML = `
          <p class="grid-archive__no-results">
            ${window.searchStrings?.nothing_found || 'No results found'}
          </p>
        `;
      } else {
        const productCards = productHits.map(hit => renderProductCard(hit)).join('');
        const articleCards = articleHits.map(hit => renderArticleCard(hit)).join('');

        let cardsHtml = productCards + articleCards;
        cardsHtml += renderPagination(currentPage, totalPages);
        gridContainer.innerHTML = cardsHtml;
        attachPaginationListeners();
      }

      const newUrl = new URL(window.location);
      newUrl.searchParams.set('q', query);
      if (page > 0) {
        newUrl.searchParams.set('page', page + 1);
      } else {
        newUrl.searchParams.delete('page');
      }
      window.history.pushState({}, '', newUrl);

      if (page > 0) {
        productGridContainer?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

    } catch (error) {
      console.error('Algolia search page error:', error);
    } finally {
      gridContainer.style.opacity = '1';
      gridContainer.style.pointerEvents = 'auto';
    }
  }

  function initSearchResultsPage() {
    if (!isSearchResultsPage() || !searchPageEnabled) return;

    document.querySelectorAll('predictive-search').forEach(ps => {
      const input = ps.querySelector('input[type="search"], input[name="q"]');
      if (input) input.value = '';
    });

    const urlParams = new URLSearchParams(window.location.search);
    const query = urlParams.get('q');
    const page = parseInt(urlParams.get('page') || '1', 10) - 1;

    if (query) {
      const gridContainer = document.getElementById('grid-product');
      if (gridContainer) {
        gridContainer.style.opacity = '0';
        gridContainer.innerHTML = `
          <div style="display: flex; justify-content: center; align-items: center; min-height: 400px; width: 100%;">
            <div style="text-align: center;">
              <div style="width: 40px; height: 40px; border: 3px solid #f3f3f3; border-top: 3px solid #333; border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto 1rem;"></div>
              <p style="color: #666;">Loading search results...</p>
            </div>
          </div>
          <style>
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          </style>
        `;
        setTimeout(() => { gridContainer.style.opacity = '1'; }, 10);
      }

      performSearchPageSearch(query, Math.max(0, page));
    }

    const searchForm = document.getElementById('FacetSearchForm');
    if (searchForm) {
      searchForm.addEventListener('submit', (e) => {
        e.preventDefault();
        const formData = new FormData(searchForm);
        const q = formData.get('q');
        if (q) performSearchPageSearch(q, 0);
      });

      const searchInput = searchForm.querySelector('input[name="q"]');
      if (searchInput) {
        let debounceTimer;
        searchInput.addEventListener('input', (e) => {
          clearTimeout(debounceTimer);
          const q = e.target.value.trim();
          if (q) {
            debounceTimer = setTimeout(() => {
              performSearchPageSearch(q, 0);
            }, 500);
          }
        });
      }
    }

    window.addEventListener('popstate', () => {
      const params = new URLSearchParams(window.location.search);
      const q = params.get('q');
      const p = parseInt(params.get('page') || '1', 10) - 1;
      if (q) performSearchPageSearch(q, Math.max(0, p));
    });
  }

  // ==========================================
  // BOOT
  // ==========================================

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAlgolia);
  } else {
    initAlgolia();
  }
})();